import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

function CompProfileForm({ profile, onSave, onCancel, isOnboarding = false }) {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const [form, setForm] = useState({
    compYear: profile?.compYear || currentYear,
    cashBonusNOK: profile?.cashBonusNOK || '',
    cashBonusPaidDate: profile?.cashBonusPaidDate?.split('T')[0] || `${currentYear}-09-25`,
    stockGrantTotalUSD: profile?.stockGrantTotalUSD || '',
    stockGrantLockDate: profile?.stockGrantLockDate?.split('T')[0] || `${currentYear}-08-31`,
    vestingStartDate: profile?.vestingStartDate?.split('T')[0] || `${currentYear}-11-30`,
    vestingYears: profile?.vestingPeriodQuarters ? profile.vestingPeriodQuarters / 4 : 5,
    marginalTaxRatePercent: profile?.marginalTaxRatePercent ?? '',
    notes: profile?.notes || '',
    signOnBonusUSD: profile?.signOnBonusUSD || '',
    signOnVestStartDate: profile?.signOnVestStartDate?.split('T')[0] || '',
    signOnVestQuarters: profile?.signOnVestQuarters || 16
  });
  const [showSignOn, setShowSignOn] = useState(isOnboarding || !!(profile?.signOnBonusUSD));

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      cashBonusNOK: parseFloat(form.cashBonusNOK) || 0,
      stockGrantTotalUSD: parseFloat(form.stockGrantTotalUSD) || 0,
      vestingYears: parseInt(form.vestingYears) || 5,
      // Blank means "don't estimate tax at all", which is different from 0%.
      marginalTaxRatePercent: form.marginalTaxRatePercent === ''
        ? null
        : parseFloat(form.marginalTaxRatePercent)
    };
    if (showSignOn && form.signOnBonusUSD) {
      payload.signOnBonusUSD = parseFloat(form.signOnBonusUSD) || null;
      payload.signOnVestStartDate = form.signOnVestStartDate || null;
      payload.signOnVestQuarters = parseInt(form.signOnVestQuarters) || 16;
    } else {
      payload.signOnBonusUSD = null;
      payload.signOnVestStartDate = null;
      payload.signOnVestQuarters = null;
    }
    onSave(payload);
  };

  return (
    <form className="profile-form" onSubmit={handleSubmit}>
      <h3>{profile ? t('compensation.editProfile') : t('compensation.addProfile')}</h3>

      {/* Show auto-fetched price info when editing an existing profile */}
      {profile?.stockPriceAtGrant > 0 && (
        <div className="auto-fetched-info">
          📈 {t('compensation.autoFetchedPrice')}: ${profile.stockPriceAtGrant?.toFixed(2)} → {profile.sharesGranted?.toFixed(2)} {t('compensation.shares')}
        </div>
      )}

      <div className="form-grid">
        <div className="form-group">
          <label>{t('compensation.compYear')}</label>
          <input type="number" value={form.compYear} onChange={e => handleChange('compYear', parseInt(e.target.value))}
            min="2000" max="2100" required />
        </div>

        <div className="form-group">
          <label>{t('compensation.cashBonus')} (NOK)</label>
          <input type="number" value={form.cashBonusNOK} onChange={e => handleChange('cashBonusNOK', e.target.value)}
            placeholder="e.g. 15000" step="100" />
        </div>

        <div className="form-group">
          <label>{t('compensation.bonusPaidDate')}</label>
          <input type="date" value={form.cashBonusPaidDate} onChange={e => handleChange('cashBonusPaidDate', e.target.value)} />
        </div>

        <div className="form-group">
          <label>{t('compensation.stockGrant')} (USD)</label>
          <input type="number" value={form.stockGrantTotalUSD} onChange={e => handleChange('stockGrantTotalUSD', e.target.value)}
            placeholder="e.g. 50000" step="100" />
        </div>

        <div className="form-group">
          <label>{t('compensation.grantLockDate')}</label>
          <input type="date" value={form.stockGrantLockDate} onChange={e => handleChange('stockGrantLockDate', e.target.value)} />
          <span className="calc-hint">{t('compensation.priceFetchedAuto')}</span>
        </div>

        <div className="form-group">
          <label>{t('compensation.vestingStart')}</label>
          <input type="date" value={form.vestingStartDate} onChange={e => handleChange('vestingStartDate', e.target.value)} />
        </div>

        <div className="form-group">
          <label>{t('compensation.vestingYears')}</label>
          <input type="number" value={form.vestingYears} onChange={e => handleChange('vestingYears', e.target.value)}
            min="1" max="10" />
        </div>

        <div className="form-group">
          <label>{t('compensation.marginalTaxRate')}</label>
          <input type="number" value={form.marginalTaxRatePercent}
            onChange={e => handleChange('marginalTaxRatePercent', e.target.value)}
            min="0" max="100" step="0.1" placeholder="47.4" />
          <span className="calc-hint">{t('compensation.marginalTaxRateHint')}</span>
        </div>

        <div className="form-group full-width">
          <label>{t('compensation.notes')}</label>
          <textarea value={form.notes} onChange={e => handleChange('notes', e.target.value)}
            placeholder={t('compensation.notesPlaceholder')} rows="2" />
        </div>
      </div>

      <div className="sign-on-section">
        <button type="button" className="btn-toggle" onClick={() => setShowSignOn(!showSignOn)}>
          {showSignOn ? '▾' : '▸'} {t('compensation.signOnBonus')}
        </button>
        {showSignOn && (
          <div className="form-grid">
            <div className="form-group">
              <label>{t('compensation.signOnAmount')} (USD)</label>
              <input type="number" value={form.signOnBonusUSD} onChange={e => handleChange('signOnBonusUSD', e.target.value)}
                min="0" step="100" placeholder="50000" />
            </div>
            <div className="form-group">
              <label>{t('compensation.signOnLockDate')}</label>
              <input type="date" value={form.signOnVestStartDate} onChange={e => handleChange('signOnVestStartDate', e.target.value)} />
              <span className="calc-hint">{t('compensation.signOnLockHint')}</span>
            </div>
            <div className="form-group">
              <label>{t('compensation.signOnTotalYears')}</label>
              <input type="number" value={form.signOnVestQuarters} onChange={e => handleChange('signOnVestQuarters', e.target.value)}
                min="1" max="40" />
              <span className="calc-hint">{t('compensation.signOnCliffHint')}</span>
            </div>
            {profile?.signOnBonusPriceAtGrant > 0 && (
              <div className="form-group">
                <div className="auto-fetched-info">
                  📈 ${profile.signOnBonusPriceAtGrant?.toFixed(2)} → {profile.signOnBonusShares?.toFixed(2)} {t('compensation.shares')}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="form-actions">
        <button type="submit" className="btn-primary">{t('common.save')}</button>
        <button type="button" className="btn-secondary" onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </form>
  );
}

export default CompProfileForm;
